import axios from "../axiosConfig";
import {
  DebuggerSessionsResponse,
  ReconstructSessionResponse,
  DebuggerReconstructRequest,
  LogSessionRequest,
  UploadBenchmarkRequest,
  CompareMessagesResponse,
  CompareMessage,
  RootNodesResponse,
  BenchmarkResults,
  BenchmarkBatchCompareRequest,
  BenchmarkPerformanceLogRequest,
  BenchmarkCompareResponse,
} from "../types/debuggerServiceTypes";
import { getCurrentPSTDateTime } from "../utils/apiUtils";
import { calculateHitRate } from "../utils/benchmarkUtils";
//Copy and past compare and change endpoint to benchmark for now
import { CompareRequestParams } from "../types/chatServiceTypes";
import { ChatMessage } from "../types/chat";

class DebuggerService {
  async getSessions(): Promise<DebuggerSessionsResponse> {
    const response = await axios.get("/api/v1/sessions");
    return response.data;
  }

  async logSession(request: LogSessionRequest): Promise<void> {
    await axios.post("/api/v1/session", request);
  }

  async getRootNodes(): Promise<RootNodesResponse> {
    const response = await axios.get("/api/v1/root-nodes");
    return response.data;
  }

  async getTreeNodes(treeUuid: string): Promise<RootNodesResponse> {
    const response = await axios.get(`/api/v1/tree-nodes/${treeUuid}`);
    return response.data;
  }

  async getCompareMessages(): Promise<CompareMessagesResponse> {
    const response = await axios.get(`/api/v1/compare-messages`);
    return response.data;
  }

  async getCompareMessage(queryId: string): Promise<CompareMessage | null> {
    try {
      const response = await axios.get(`/api/v1/compare-messages/${queryId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching compare message:", error);
      return null;
    }
  }

  async runSingleBenchmarkCompare(
    message: CompareMessage,
    prefix: string = "benchmark/"
  ): Promise<
    | BenchmarkCompareResponse
    | { action: string; endpoint_result?: string; distance?: number }
  > {
    // message.compare_request is a string, so parse it to get CompareRequestParams
    let params: CompareRequestParams;
    let result;
    try {
      // Parse the compare_request if it's a string
      const rawParams =
        typeof message.compare_request === "string"
          ? JSON.parse(message.compare_request)
          : message.compare_request;

      // Map rawParams to the expected CompareRequestParams shape
      params = {
        userMessage: rawParams.user_message ?? rawParams.userMessage ?? "",
        options: rawParams.options ?? [],
        nodeUUID: rawParams.current_node_id ?? rawParams.nodeUUID ?? "",
        actions: rawParams.actions ?? [],
        links: rawParams.links ?? [],
        userEmail: rawParams.user_email ?? rawParams.userEmail ?? "",
        chatHistory: rawParams.chatHistory ?? [],
        idHistory: rawParams.idHistory ?? [],
        queryId: rawParams.query_id ?? rawParams.queryId,
        pruned_expected_response: message.pruned_expected_response,
        expected_response: message.expected_response,
      };

      result = await this.makeCompareBenchmarkRequest(params, prefix);

      // Convert the result to BenchmarkCompareResponse format
      if ("action" in result) {
        return {
          action: result.action,
          endpoint_result: result.endpoint_result,
          distance: result.distance,
        };
      } else {
        return {
          new_reply_node_uuid: result.nodeUuid || "",
          query_id: message.query_id,
          text: result.text || "",
          options: result.options || [],
          question: result.origin || "",
          audio: result.audio || [],
          media: result.media || null,
          endpoint_result: result.endpoint_result || "",
          distance: result.distance,
        };
      }
    } catch (error) {
      console.error("Error running single benchmark compare:", error);
      throw error;
    }
  }

  async updateCompareMessage(
    queryId: string,
    updates: Partial<CompareMessage>
  ): Promise<void> {
    await axios.post("/api/v1/log", {
      query_id: queryId,
      ...updates,
    });
  }

  async reconstructSession(
    request: DebuggerReconstructRequest
  ): Promise<ReconstructSessionResponse> {
    const response = await axios.get("/api/v1/reconstruct-session", {
      params: {
        session_id: request.session_id,
      },
    });
    return response.data;
  }

  async uploadBenchmarkDatabase(
    request: UploadBenchmarkRequest
  ): Promise<void> {
    if (!Object.keys(request.files).length) return;

    const res = await axios.post("/api/v1/benchmark/files", request);
    if (res.status !== 200) {
      throw new Error(`Upload failed: HTTP ${res.status}`);
    }
  }

  async makeCompareBenchmarkRequest(
    data: CompareRequestParams,
    prefix: string = "benchmark/"
  ): Promise<
    | ChatMessage
    | { action: string; endpoint_result?: string; distance?: number }
  > {
    const sessionID = "Benchmark session ID";
    const endpoint = "/api/v1/benchmark/compare";
    const currentDateTime = getCurrentPSTDateTime();
    const request = {
      query_id: data.queryId || crypto.randomUUID(),
      user_email: "benchmark email",
      session_id: sessionID,
      current_node_id: data.nodeUUID,
      user_message: data.userMessage,
      options: data.options,
      links: data.links || [],
      actions: data.actions || [],
      user_message_sent_datetime: currentDateTime,
      chatHistory: data.chatHistory,
      idHistory: data.idHistory,
      pruned_expected_response: data.pruned_expected_response,
      expected_response: data.expected_response,
    };
    try {
      const response = await axios.post(
        `${endpoint}?prefix=${encodeURIComponent(prefix)}`,
        request
      );
      if (response.status !== 200)
        throw new Error(
          `HTTP error! status: ${response.status} - ${response.statusText}`
        );
      const responseData = response.data;
      if (responseData.action) {
        return {
          action: responseData.action,
          endpoint_result: responseData.endpoint_result,
          distance: responseData.distance,
        };
      } else if (responseData.text) {
        return {
          id: crypto.randomUUID(),
          messageId: responseData.query_id,
          text: responseData.text,
          dateTime: new Date(),
          messageType: "bot" as const,
          nodeUuid: responseData.new_reply_node_uuid,
          options: responseData.options || [],
          audio: responseData.audio
            ? Array.isArray(responseData.audio)
              ? responseData.audio
              : [responseData.audio]
            : [],
          media: responseData.media,
          origin: responseData.question,
          endpoint_result: responseData.endpoint_result,
          distance: responseData.distance,
          isFlagged: false,
        };
      } else {
        throw new Error(
          `Unknown response type: ${JSON.stringify(responseData)}`
        );
      }
    } catch (error) {
      console.error("Compare request failed:", error);
      return {
        id: "00000000-0000-0000-0000-000000000000",
        messageId: data.queryId || "00000000-0000-0000-0000-000000000000",
        text: "An error occurred. Please try again.",
        dateTime: new Date(),
        messageType: "bot" as const,
        options: [],
        audio: [],
        media: "",
        isFlagged: false,
      };
    }
  }

  async runChunkedBatchBenchmark(
    prefix: string = "benchmark/",
    chunkSize: number = 5,
    onProgress?: (chunkNumber: number, totalMessages: number) => void,
    benchmarkDatetime?: string
  ): Promise<BenchmarkResults> {
    try {
      let aggregatedResults: BenchmarkResults = {
        num_previously_matched_now_matched: 0,
        num_previously_matched_now_mismatched: 0,
        num_previously_mismatched_now_matched: 0,
        num_previously_mismatched_now_mismatched: 0,
        num_previously_unconfident_now_confident: 0,
        num_previously_unconfident_now_unconfident: 0,
        num_correct_match_did_not_prune_enough: 0,
        num_correct_match_over_pruned: 0,
        num_correct_prune: 0,
        num_did_not_get_right_tree_or_prune: 0,
        needs_a_new_tree_count: 0,
        total_messages: 0,
        new_message_responses: {},
      };

      let startDatetime: string | undefined = undefined;
      let hasMoreMessages = true;
      let chunkCount = 0;

      while (hasMoreMessages) {
        chunkCount++;

        const requestBody: BenchmarkBatchCompareRequest = {
          prefix: prefix,
          chunk_size: chunkSize,
          start_datetime: startDatetime,
          benchmark_datetime: benchmarkDatetime,
        };

        const response = await axios.post(
          `/api/v1/benchmark/batch-compare`,
          requestBody
        );

        const chunkResults: BenchmarkResults = response.data;

        // Aggregate results
        aggregatedResults.num_previously_matched_now_matched +=
          chunkResults.num_previously_matched_now_matched;
        aggregatedResults.num_previously_matched_now_mismatched +=
          chunkResults.num_previously_matched_now_mismatched;
        aggregatedResults.num_previously_mismatched_now_matched +=
          chunkResults.num_previously_mismatched_now_matched;
        aggregatedResults.num_previously_mismatched_now_mismatched +=
          chunkResults.num_previously_mismatched_now_mismatched;
        aggregatedResults.needs_a_new_tree_count +=
          chunkResults.needs_a_new_tree_count;
        aggregatedResults.num_previously_unconfident_now_confident +=
          chunkResults.num_previously_unconfident_now_confident;
        aggregatedResults.num_previously_unconfident_now_unconfident +=
          chunkResults.num_previously_unconfident_now_unconfident;
        aggregatedResults.total_messages += chunkResults.total_messages;
        aggregatedResults.num_correct_match_did_not_prune_enough +=
          chunkResults.num_correct_match_did_not_prune_enough;
        aggregatedResults.num_correct_match_over_pruned +=
          chunkResults.num_correct_match_over_pruned;
        aggregatedResults.num_correct_prune += chunkResults.num_correct_prune;
        aggregatedResults.num_did_not_get_right_tree_or_prune +=
          chunkResults.num_did_not_get_right_tree_or_prune;

        // Merge new message responses
        if (chunkResults.new_message_responses) {
          aggregatedResults.new_message_responses = {
            ...aggregatedResults.new_message_responses,
            ...chunkResults.new_message_responses,
          };
        }

        // Check if there are more messages to process
        hasMoreMessages = chunkResults.has_more_messages || false;
        startDatetime = chunkResults.next_start_datetime;

        // Call progress callback if provided
        if (onProgress) {
          onProgress(chunkCount, aggregatedResults.total_messages);
        }

        // Add a small delay between chunks to prevent overwhelming the server
        if (hasMoreMessages) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

      // Log final results to benchmark performance table
      await this.logBenchmarkPerformanceResults(
        prefix,
        benchmarkDatetime || new Date().toISOString(),
        aggregatedResults
      );

      return aggregatedResults;
    } catch (error) {
      console.error("Chunked batch benchmark failed:", error);
      throw error;
    }
  }

  private async logBenchmarkPerformanceResults(
    prefix: string,
    benchmarkDatetime: string,
    results: BenchmarkResults
  ): Promise<void> {
    try {
      const performanceData: BenchmarkPerformanceLogRequest = {
        benchmark_prefix: prefix,
        benchmark_datetime: benchmarkDatetime,
        hit_rate: calculateHitRate(results),
        total_messages: results.total_messages,
        total_needing_a_new_tree: results.needs_a_new_tree_count,
        num_matches_prev_mismatched:
          results.num_previously_mismatched_now_matched,
        num_mismatches_prev_mismatched:
          results.num_previously_mismatched_now_mismatched,
        num_matches_prev_matched: results.num_previously_matched_now_matched,
        num_mismatches_prev_matched:
          results.num_previously_matched_now_mismatched,
        num_prev_unconfident_now_confident:
          results.num_previously_unconfident_now_confident,
        num_prev_unconfident_now_unconfident:
          results.num_previously_unconfident_now_unconfident,
        num_correct_match_did_not_prune_enough:
          results.num_correct_match_did_not_prune_enough,
        num_correct_match_over_pruned: results.num_correct_match_over_pruned,
        num_correct_prune: results.num_correct_prune,
        num_did_not_get_right_tree_or_prune:
          results.num_did_not_get_right_tree_or_prune,
      };

      // Send to backend to log to DynamoDB
      await axios.post("/api/v1/benchmark/log-performance", performanceData);
    } catch (error) {
      console.error("Failed to log benchmark performance results:", error);
      // Don't throw error to avoid breaking the main benchmark flow
    }
  }

  async getLastBenchmarkResults(): Promise<BenchmarkResults> {
    try {
      const response = await axios.get(
        "/api/v1/benchmark/last-benchmark-results"
      );
      return response.data;
    } catch (error) {
      console.error("Failed to get last benchmark results:", error);
      throw error;
    }
  }
}

export const debuggerService = new DebuggerService();
